import React, { useState, useRef, useEffect } from 'react';
import { updateUser } from '../api';
import ThemeEditor from './ThemeEditor';
import toast from 'react-hot-toast';

const UserProfile = ({ user, token, onLogout, onThemeChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
        setShowThemeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopyToken = () => {
    navigator.clipboard.writeText(token);
    toast.success('Token copied');
    setShowMenu(false);
    setShowThemeMenu(false);
  };

  const handleLogoutClick = () => {
    setShowMenu(false);
    setShowThemeMenu(false);
    onLogout();
  };

  const handleThemeSelect = async (themeName) => {
    try {
      await updateUser(user.id, { theme: themeName });
      onThemeChange(themeName);
      toast.success('Theme updated');
    } catch (err) {
      toast.error('Failed to save theme');
    }
  };

  const handleCustomThemeSave = async (colors) => {
    try {
      await updateUser(user.id, { theme: 'custom', custom_theme: colors });
      onThemeChange('custom', colors);
      setShowThemeEditor(false);
      toast.success('Custom theme saved');
    } catch (err) {
      toast.error('Failed to save custom theme');
    }
  };

  const avatarUrl = user?.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png';

  const themes = [
    { id: 'dark', name: 'Dark (Default)' },
    { id: 'amoled', name: 'AMOLED Black' },
    { id: 'light', name: 'Light Mode' },
    { id: 'midnight', name: 'Midnight Blue' }
  ];

  return (
    <div className="user-profile-wrapper" ref={menuRef}>
      <button className="user-avatar-btn" onClick={() => { setShowMenu(!showMenu); setShowThemeMenu(false); }} title={user?.username}>
        <img src={avatarUrl} alt="avatar" />
        <div className="status-dot"></div>
      </button>

      {showMenu && !showThemeMenu && (
        <div className="user-popup">
          <div className="user-popup-header">
            <img src={avatarUrl} alt="avatar" />
            <div className="user-info">
              <strong>{user?.username}</strong>
              <span>{user?.discord_id}</span>
            </div>
          </div>
          <div className="user-popup-actions">
            <button className="btn btn-gray" onClick={() => setShowThemeMenu(true)} style={{ width: '100%', justifyContent: 'space-between' }}>
              <span>Themes</span>
              <span>▶</span>
            </button>
            <button className="btn btn-blurple" onClick={handleCopyToken} style={{ width: '100%' }}>
              Copy Discord Token
            </button>
            <button className="btn btn-red" onClick={handleLogoutClick} style={{ width: '100%' }}>
              Log Out
            </button>
          </div>
        </div>
      )}

      {showThemeMenu && (
        <div className="user-popup theme-popup">
          <div className="user-popup-header" style={{ cursor: 'pointer', paddingBottom: '12px', borderBottom: '1px solid var(--border-subtle)' }} onClick={() => setShowThemeMenu(false)}>
            <strong style={{ color: 'var(--text-muted)' }}>◀ Back</strong>
          </div>
          <div className="user-popup-actions" style={{ marginTop: '12px' }}>
            {themes.map(t => (
              <button 
                key={t.id} 
                className={`btn ${user?.theme === t.id || (!user?.theme && t.id === 'dark') ? 'btn-blurple' : 'btn-gray'}`} 
                onClick={() => handleThemeSelect(t.id)}
                style={{ width: '100%', justifyContent: 'flex-start' }}
              >
                {t.name}
              </button>
            ))}
            <div style={{ height: '1px', backgroundColor: 'var(--border-subtle)', margin: '4px 0' }}></div>
            <button 
              className={`btn ${user?.theme === 'custom' ? 'btn-blurple' : 'btn-gray'}`}
              onClick={() => { setShowThemeEditor(true); setShowMenu(false); setShowThemeMenu(false); }}
              style={{ width: '100%', justifyContent: 'flex-start' }}
            >
              Custom Theme...
            </button>
          </div>
        </div>
      )}

      {showThemeEditor && (
        <ThemeEditor 
          initialCustomTheme={user?.custom_theme}
          onSave={handleCustomThemeSave} 
          onCancel={() => setShowThemeEditor(false)} 
        />
      )}
    </div>
  );
};

export default UserProfile;
